import React from 'react';
import styled from 'styled-components';

/** Renders the mark 45% larger than the requested height. */
export const LOGO_SCALE = 1.45;

const Mark = styled.svg<{ $height: number }>`
  display: block;
  height: ${({ $height }) => $height}px;
  width: auto;
  flex-shrink: 0;
`;

export interface SpotdoveLogoProps {
  /** Base height in pixels before {@link LOGO_SCALE} is applied. */
  height?: number;
  className?: string;
  /** Accessible label when the logo is meaningful on its own. */
  title?: string;
  /** When true, hides the logo from assistive tech (e.g. next to visible wordmark text). */
  decorative?: boolean;
}

/** Dove head + script waves — Spotdove brand mark. */
const SpotdoveLogo: React.FC<SpotdoveLogoProps> = ({
  height = 32,
  className,
  title = 'Spotdove',
  decorative = false,
}) => (
  <Mark
    $height={height * LOGO_SCALE}
    className={className}
    viewBox="0 0 500 500"
    xmlns="http://www.w3.org/2000/svg"
    role={decorative ? 'presentation' : 'img'}
    aria-hidden={decorative ? true : undefined}
    aria-label={decorative ? undefined : title}
  >
    {!decorative && <title>{title}</title>}
    <path
      fill="none"
      stroke="#54725E"
      strokeWidth="30"
      strokeLinecap="round"
      d="M 35.556 217.31 C 35.556 217.31 42.922 132.728 105.568 118.816 C 148.657 109.247 191.912 145.381 195.893 172.264 C 200 200 196.172 216.931 176.488 230.371 C 163.302 239.374 166.014 267.793 163.006 286.807 C 154.091 343.156 105.431 383.768 105.431 383.768"
    />
    <ellipse cx="137.991" cy="179.256" rx="18.583" ry="18.583" fill="#135C28" />
    <path
      fill="#54725E"
      d="M 169.733 269.383 C 169.733 269.383 195.132 234.012 223.851 232.53 C 256.58 230.841 300 231.857 300 231.857 C 300 231.857 271.963 219.223 243.541 200 C 227.529 189.171 214.476 186.201 200 171.806 C 180.928 152.841 169.733 269.383 169.733 269.383 Z"
    />
    <path
      fill="none"
      stroke="#85D8B5"
      strokeWidth="30"
      strokeLinecap="round"
      d="M 304.956 213.157 C 304.956 213.157 328.686 229.734 349.126 214.264 C 370.293 198.244 387.561 210.198 394.907 214.247 C 423.919 230.237 436.424 212.356 436.424 212.356"
    />
    <path
      fill="none"
      stroke="#849A90"
      strokeWidth="30"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M 206.9 265.921 C 206.9 265.921 233.053 245.858 259.699 266.313 C 285.323 285.983 299.486 257.37 315.178 256.446 C 333.59 255.362 345.105 271.895 357.506 270.39 C 378.168 267.883 374.302 258.483 394.829 259.579 C 410.37 260.408 411.509 262.991 420.468 263.462 C 432.085 264.071 438.498 261.965 438.498 261.965"
    />
    <path
      fill="#B3D0A4"
      stroke="#B3D0A4"
      strokeWidth="6"
      strokeLinecap="round"
      d="M 449.596 95.509 C 449.596 95.509 430.575 104.418 415.081 104.518 C 406.676 104.572 385.729 92.036 383.06 100 C 380.656 107.173 394.165 137.081 384.799 171.947 C 382.576 180.222 405.224 159.591 417.472 168.548 C 430.428 178.024 449.567 168.76 449.567 168.76"
    />
    <path
      fill="none"
      stroke="#85D8B5"
      strokeWidth="30"
      strokeLinecap="round"
      d="M 226.406 161.941 C 226.406 161.941 246.724 144.182 274.282 163.391 C 296.06 178.571 308.656 171.252 318.172 164.775 C 342.439 148.256 365.42 163.163 365.42 163.163"
    />
    <path
      fill="none"
      stroke="#85D8B5"
      strokeWidth="30"
      strokeLinecap="round"
      d="M 222.456 114.094 C 222.456 114.094 242.774 96.335 270.332 115.544 C 292.11 130.724 304.706 123.405 314.222 116.928 C 338.489 100.409 361.47 115.316 361.47 115.316"
    />
    <path
      fill="none"
      stroke="#849A90"
      strokeWidth="30"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M 206.329 317.399 C 206.329 317.399 232.482 297.336 259.128 317.791 C 284.752 337.461 298.915 308.848 314.607 307.924 C 333.019 306.84 344.534 323.373 356.935 321.868 C 377.597 319.361 373.731 309.961 394.258 311.057 C 409.799 311.886 410.938 314.469 419.897 314.94 C 431.514 315.549 437.927 313.443 437.927 313.443"
    />
    <path
      fill="none"
      stroke="#849A90"
      strokeWidth="30"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M 206.233 365.31 C 206.233 365.31 232.386 345.247 259.032 365.702 C 284.656 385.372 298.819 356.759 314.511 355.835 C 332.923 354.751 344.438 371.284 356.839 369.779 C 377.501 367.272 373.635 357.872 394.162 358.968 C 409.703 359.797 410.842 362.38 419.801 362.851 C 431.418 363.46 437.831 361.354 437.831 361.354"
    />
  </Mark>
);

export default SpotdoveLogo;
